import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  Avatar,
  MenuItem,
  Select as MuiSelect,
  type SelectChangeEvent,
  styled,
} from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';

import { createTanstackTable, ProjectRolesOptions } from '../../common/utils';
import { useGetOrganizations } from '../../hooks/tanstack/useOrganizationHook';
import {
  useGetProjectById,
  useGetProjectMembers,
  useUpdateProjectMember,
} from '../../hooks/tanstack/useProjectsHook';
import { useGetUser } from '../../hooks/tanstack/useUserHook';
import {
  isOrgAdmin,
  useGetRouteParams,
  useGetSearchResults,
} from '../../hooks/tanstack/useUtilsHook';
import {
  type OrganizationStatus,
  type ProjectRole,
  type Role,
  type UserStatus,
} from '../../types';
import cn from '../../utils/cn';
import { transformToLabel } from '../../utils/helpers';
import TanstackTable from '../TanstackTable';
import ConfirmLeaveProjectDialog from '../userManagement/ConfirmLeaveProjectDialog';
import ResendOrgInvitation from '../userManagement/ResendOrgInvitation';

export type DataRow = {
  id: number;
  user: {
    id: number;
    name?: string | null;
    avatarImage: string;
    email: string;
  };
  type: OrganizationStatus;
  role: ProjectRole;
  lastActive: string | null;
  status: UserStatus;
};

const Select = styled(MuiSelect)({
  padding: '0px !important',
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none !important',
    padding: '0px !important',
  },

  '& .MuiSelect-select': {
    backgroundColor: '#F8F8F8',
    paddingLeft: '10px !important',
    paddingRight: '60px !important',
  },
  '& .MuiSelect-nativeInput': {},
});

const columnHelper = createColumnHelper<DataRow>();

const columns = [
  columnHelper.accessor('user', {
    cell: (info) => {
      const { email, name, avatarImage } = info.getValue();

      return (
        <div className="group flex items-center gap-1 text-start max-w-[160px]">
          <div className="flex items-center gap-2">
            <Avatar
              alt="User avatar"
              src={avatarImage}
              sx={{
                height: 40,
                width: 40,
                rounded: 'full',
              }}
            />
            <div className="flex flex-col">
              <div className="flex w-full justify-between gap-3">
                <p
                  className={cn('font-semibold', {
                    'opacity-70': !name,
                  })}
                >
                  {name ?? email?.split('@')?.[0]}
                </p>
              </div>
              <p className="text-sm text-[#666]">{email}</p>
            </div>
          </div>
        </div>
      );
    },
    header: (info) => (
      <div className="flex items-center gap-1">
        <p>User</p>
      </div>
    ),
  }),
  columnHelper.accessor('lastActive', {
    id: 'lastActive',
    cell: (info) =>
      info.getValue() ? (
        <div className="w-full">
          {format(info.getValue() as string, 'y/MM/dd hh:mm a')}
        </div>
      ) : (
        <div className="text-gray-400 w-full text-start">--</div>
      ),
    sortingFn: (a, b) =>
      new Date(a.original.lastActive as string).getTime() -
      new Date(b.original.lastActive as string).getTime(),
    header: () => <div>Last active</div>,
  }),
  columnHelper.accessor('role', {
    id: 'role',
    cell: (info) => {
      const memberId = info.row.original.id;
      const email = info.row.original.user.email;
      const type = info.row.original.type;

      const [role, setRole] = useState<Role | null>(null);

      const { orgId, projectId } = useGetRouteParams();

      const { data: currentUser } = useGetUser();

      const { data: organizations } = useGetOrganizations();

      const { mutate: updateProjectMember } = useUpdateProjectMember();

      useEffect(() => {
        setRole(info.getValue());
      }, [info.getValue()]);

      const handleChangeRole = (event: SelectChangeEvent<unknown>) => {
        if (!orgId) {
          return;
        }

        const selectedRole = event.target.value as Role;

        if (type === 'member') {
          updateProjectMember({
            orgId,
            projectId,
            memberId,
            data: {
              role: selectedRole,
            },
          });
        }

        setRole(selectedRole);
      };

      const orgMember = organizations?.find((o) => o.id === orgId);

      return (
        <div className="max-w-[190px]">
          {currentUser?.email !== email &&
          orgMember?.role !== 'organization_member' ? (
            <Select
              id="role"
              size="small"
              value={role}
              disabled={currentUser?.email === email}
              onChange={handleChangeRole}
              className="bottom-0"
              fullWidth
              placeholder="Please select"
            >
              {ProjectRolesOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <span className="capitalize">{option.label}</span>
                </MenuItem>
              ))}
            </Select>
          ) : (
            <div className="flex items-center justify-between rounded-[4px] bg-[#F8F8F8] p-2 capitalize">
              <p>{role && transformToLabel(role)}</p>
            </div>
          )}
        </div>
      );
    },
    header: () => <div>Role</div>,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    cell: (info) => {
      const status = info.getValue();

      const { projectId, orgId } = useGetRouteParams();

      const type = info.row.original.type;

      const { data: project } = useGetProjectById(
        {
          orgId,
          projectId,
        },
        {
          enabled: !!orgId && !!projectId,
        }
      );

      const { data: organizations } = useGetOrganizations();
      const { data: currentUser } = useGetUser();

      const currentOrgMember = organizations?.find((org) => org.id === orgId);

      const projectRole = project?.role;

      const row = {
        ...info.row.original,
        email: info.row.original.user.email,
      };

      const user = {
        ...row,
        type:
          status === 'pending'
            ? 'invitation'
            : ('member' as 'invitation' | 'member'),
      };

      const isAllowEdit =
        (user?.role === 'project_member' && projectRole === 'project_owner') ||
        user.user.id === currentUser?.id ||
        (currentOrgMember && isOrgAdmin(currentOrgMember.role));

      const StatusLabel = ({
        label,
        value,
      }: {
        value: 'active' | 'pending';
        label: string;
      }) => (
        <div className="flex justify-start">
          <p
            className={cn(
              'w-fit rounded-full bg-[#E1FBE7] px-6 text-green-600',
              {
                'bg-[#EEF6FF] text-[#117DD3]': value === 'active',
                'text-[#C38D04] bg-[#FFF8E0]': value === 'pending',
              }
            )}
          >
            {label}
          </p>
        </div>
      );

      return (
        <div className="flex w-full min-w-[200px] items-center justify-between">
          {status === 'active' ? (
            <StatusLabel value="active" label="Active" />
          ) : null}
          {status === 'pending' ? (
            <StatusLabel value="pending" label="Pending" />
          ) : null}
          {isAllowEdit && (
            <div className="flex gap-4">
              {type === 'invitation' && (
                <ResendOrgInvitation invitationId={row.id} orgId={orgId} />
              )}
              {project && (
                <ConfirmLeaveProjectDialog user={user} project={project} />
              )}
            </div>
          )}
        </div>
      );
    },
    header: () => <div>Status</div>,
    size: 200,
  }),
];

const ProjectMembersTable = ({
  searchKey,
  filters,
}: {
  searchKey: string;
  filters: {
    role?: ProjectRole;
  };
}) => {
  const [data, setData] = useState<DataRow[]>([]);

  const { projectId, orgId } = useGetRouteParams();

  const {
    data: members,
    isLoading: isFetchingProjectMembers,
    isRefetching: isRefetchingProjectMembers,
  } = useGetProjectMembers(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!orgId && !!projectId,
    }
  );

  const form = useForm();
  const { watch } = form;

  const selectedUsers = watch('selectedUsers');

  const filteredResults = useMemo(() => {
    const projectRole = filters.role;

    if (!projectRole) {
      return data;
    }

    return data.filter((d) => d.role === projectRole);
  }, [data, filters]);

  const searchResults = useGetSearchResults<DataRow>({
    searchValue: searchKey,
    data: filteredResults,
    filterFields: ['user.name', 'user.email', 'role'],
  });

  useEffect(() => {
    const dataRows: DataRow[] = [];

    if (members && members?.length > 0) {
      members.forEach((m) =>
        dataRows.push({
          id: m.id,
          lastActive: m.date_joined,
          user: {
            id: m.user.id,
            name: `${m.user.first_name} ${m.user.last_name}`,
            avatarImage: m.user.profile_pic,
            email: m.user.email,
          },
          type: 'member',
          role: m.role,
          status: 'active',
        })
      );
    }

    setData(dataRows);
  }, [members]);

  const table = createTanstackTable<DataRow>({
    data: searchResults ?? data,
    columns,
  });

  const isLoading = isFetchingProjectMembers || isRefetchingProjectMembers;

  return (
    <div className="w-full">
      <FormProvider {...form}>
        <form>
          <TanstackTable
            table={table}
            isLoading={isLoading}
            emptyState={
              searchKey
                ? 'No results'
                : 'No users available. Please add a user.'
            }
          />
        </form>
      </FormProvider>
      <div className="h-4" />
    </div>
  );
};

export default ProjectMembersTable;
